// src/pages/admin/SystemSettings.tsx - MODERN UI
import React, { useState, useEffect, useRef } from 'react';
import {
  Settings, Save, Bell, Mail, Clock, Shield,
  Database, Globe, Palette, AlertTriangle,
  Info, Wrench, RefreshCw, HardDrive, Server, Upload,
  Check, X, ChevronRight, Zap, Lock, Eye,
  FileText, CheckCircle, AlertCircle, Trash2,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Input, Textarea, Loading, LogoLoadingMark, LoadingLabel } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService, AdminSystemInfo } from '../../services/admin';
import { getAssetUrl } from '../../utils/url';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { normalizeSystemUptime } from '../../utils/systemInfo';
import { normalizeSystemHealth } from '../../utils/systemHealth';

// ========== TYPES ==========

interface SettingGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

// ========== SETTING GROUPS ==========

const settingGroups: SettingGroup[] = [
  { id: 'general', label: 'Umum', icon: <Globe className="w-5 h-5" />, description: 'Informasi institusi & aplikasi', color: 'from-blue-500 to-blue-600' },
  { id: 'notification', label: 'Notifikasi', icon: <Bell className="w-5 h-5" />, description: 'Email & pengingat', color: 'from-amber-500 to-amber-600' },
  { id: 'workflow', label: 'Workflow', icon: <Clock className="w-5 h-5" />, description: 'SLA & eskalasi', color: 'from-purple-500 to-purple-600' },
  { id: 'security', label: 'Keamanan', icon: <Shield className="w-5 h-5" />, description: 'Password & sesi', color: 'from-rose-500 to-rose-600' },
  { id: 'appearance', label: 'Tampilan', icon: <Palette className="w-5 h-5" />, description: 'Logo & branding', color: 'from-teal-500 to-teal-600' },
  { id: 'maintenance', label: 'Sistem', icon: <Wrench className="w-5 h-5" />, description: 'Info & maintenance', color: 'from-gray-600 to-gray-700' },
];

const defaultSettings: Record<string, any> = {
  // General
  app_name: '',
  app_description: '',
  institution_name: '',
  institution_code: '',
  institution_address: '',
  institution_phone: '',
  institution_email: '',
  institution_website: '',
  // Notification
  email_enabled: true,
  email_from: '',
  email_from_name: '',
  notif_submission: true,
  notif_validation: true,
  notif_rapim: true,
  notif_overdue: true,
  notif_reminder_days: '7,3,1',
  // Workflow
  sla_rendah: 60,
  sla_sedang: 30,
  sla_tinggi: 14,
  sla_critical: 7,
  auto_escalation: true,
  escalation_after_days: 3,
  auto_archive_days: 90,
  max_file_size: 10,
  max_files_per_report: 10,
  // Security
  password_min_length: 8,
  password_require_uppercase: true,
  password_require_number: true,
  password_require_special: true,
  session_lifetime: 120,
  // Appearance
  primary_color: '#0f33ab',
  logo_url: '',
  favicon_url: '',
  login_background: '',
  footer_text: '',
};

// ========== CUSTOM TOGGLE COMPONENT ==========

const Toggle: React.FC<{
  value: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}> = ({ value, onChange, label, description, icon }) => (
  <div className="flex items-center justify-between py-3 group">
    <div className="flex items-start gap-3">
      {icon && (
        <div className={`p-1.5 rounded-lg mt-0.5 ${value ? 'bg-teal-100 text-teal-600' : 'bg-neutral-bg text-neutral-text-secondary'} transition-colors`}>
          {icon}
        </div>
      )}
      <div>
        <p className="text-sm font-semibold text-neutral-text-primary">{label}</p>
        {description && <p className="text-xs text-neutral-text-secondary mt-0.5">{description}</p>}
      </div>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative w-12 h-7 rounded-full transition-all duration-300 shrink-0 ${value
        ? 'bg-teal-main shadow-inner'
        : 'bg-neutral-border'
        }`}
    >
      <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${value ? 'left-6' : 'left-1'
        }`}>
        {value ? (
          <Check className="w-3 h-3 text-teal-main absolute top-1 left-1" />
        ) : (
          <X className="w-3 h-3 text-neutral-text-secondary absolute top-1 left-1" />
        )}
      </div>
    </button>
  </div>
);

// ========== SECTION HEADER COMPONENT ==========

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  color?: string;
}> = ({ icon, title, description, color = 'text-teal-main bg-teal-main/10' }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`p-2.5 rounded-xl ${color}`}>
      {icon}
    </div>
    <div>
      <h3 className="text-base font-bold text-teal-dark">{title}</h3>
      {description && <p className="text-xs text-neutral-text-secondary mt-0.5">{description}</p>}
    </div>
  </div>
);

// ========== MAIN COMPONENT ==========

const SystemSettings: React.FC = () => {
  const [activeGroup, setActiveGroup] = useState('general');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingLoginBg, setUploadingLoginBg] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const loginBgInputRef = useRef<HTMLInputElement>(null);

  // System Info
  const [systemInfo, setSystemInfo] = useState<AdminSystemInfo | null>(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceAction, setMaintenanceAction] = useState<'clearCache' | 'backup' | null>(null);

  // Clean Data Modal State
  const [showCleanDataModal, setShowCleanDataModal] = useState(false);
  const [cleaningData, setCleaningData] = useState(false);
  const [cleanConfirmText, setCleanConfirmText] = useState('');
  const [cleanStep, setCleanStep] = useState(1);

  // Settings state
  const [settings, setSettings] = useState<Record<string, any>>(() => ({ ...defaultSettings }));

  // Track original settings for change detection
  const [originalSettings, setOriginalSettings] = useState<Record<string, any>>({});

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const res = await adminService.settings.getAll();
        const newSettings: Record<string, any> = { ...defaultSettings };
        res.forEach(item => {
          if (item.type === 'boolean') {
            newSettings[item.key] = item.value === '1' || item.value === 'true';
          } else if (item.type === 'number' || item.type === 'int') {
            newSettings[item.key] = Number(item.value);
          } else {
            newSettings[item.key] = item.value;
          }
        });
        setSettings(newSettings);
        setOriginalSettings({ ...newSettings });
      } catch (e) {
        toast.error('Gagal memuat pengaturan');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  useEffect(() => {
    if (activeGroup === 'maintenance') {
      fetchSystemInfo();
    }
  }, [activeGroup]);

  // Detect changes
  useEffect(() => {
    const changed = Object.keys(settings).some(key => {
      return String(settings[key]) !== String(originalSettings[key]);
    });
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const fetchSystemInfo = async () => {
    try {
      const info = await adminService.system.getInfo();
      setSystemInfo(info);
    } catch (e: any) {
      console.error('[SystemSettings] Gagal memuat system info:', e?.message || e);
      setSystemInfo(null);
    }
  };

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const uptimeInfo = normalizeSystemUptime(systemInfo);
  const systemHealth = normalizeSystemHealth(systemInfo?.health ?? (
    systemInfo
      ? {
        status: 'warning',
        summary: 'Data System Health belum dikirim backend.',
        checks: [],
      }
      : null
  ));
  const responseTimeMs = Number(systemInfo?.response_time_ms);
  const responseTimeLabel = Number.isFinite(responseTimeMs) && responseTimeMs > 0
    ? `${responseTimeMs} ms`
    : (systemInfo ? 'N/A' : 'Error');
  const healthTone = systemHealth.status === 'healthy'
    ? {
      card: 'border-green-200 bg-green-50',
      text: 'text-green-700',
      pill: 'bg-green-100 text-green-700',
    }
    : systemHealth.status === 'warning'
      ? {
        card: 'border-amber-200 bg-amber-50',
        text: 'text-amber-700',
        pill: 'bg-amber-100 text-amber-700',
      }
      : {
        card: 'border-red-200 bg-red-50',
        text: 'text-red-700',
        pill: 'bg-red-100 text-red-700',
      };

  const handleSave = async () => {
    // Validasi
    const errors: string[] = [];

    const sessionLifetime = Number(settings.session_lifetime);
    if (sessionLifetime < 5) errors.push('Session lifetime minimal 5 menit');
    if (sessionLifetime > 1440) errors.push('Session lifetime maksimal 1440 menit (24 jam)');

    const pwdMin = Number(settings.password_min_length);
    if (pwdMin < 6) errors.push('Password minimal 6 karakter');
    if (pwdMin > 32) errors.push('Password maksimal 32 karakter');

    if (Number(settings.sla_rendah) <= Number(settings.sla_sedang)) {
      errors.push('SLA Rendah harus lebih besar dari SLA Sedang');
    }
    if (Number(settings.sla_sedang) <= Number(settings.sla_tinggi)) {
      errors.push('SLA Sedang harus lebih besar dari SLA Tinggi');
    }
    if (Number(settings.sla_tinggi) <= Number(settings.sla_critical)) {
      errors.push('SLA Tinggi harus lebih besar dari SLA Kritis');
    }

    if (errors.length > 0) {
      toast.error('Validasi gagal:\n• ' + errors.join('\n• '), { duration: 6000 });
      return;
    }

    setSaving(true);
    try {
      const batchData = Object.keys(settings).map(key => ({
        key,
        value: typeof settings[key] === 'boolean' ? (settings[key] ? '1' : '0') : String(settings[key])
      }));

      await adminService.settings.updateBatch(batchData);

      const { updateSettings } = useSystemSettingsStore.getState();
      // Update all settings in the store, not just branding
      updateSettings({ ...settings });

      setOriginalSettings({ ...settings });
      setHasChanges(false);
      toast.success('Pengaturan berhasil disimpan');
    } catch (e) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await adminService.settings.sendTestEmail();
      toast.success(res?.message || 'Email uji coba berhasil dikirim');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal mengirim email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleMaintenanceAction = async (action: 'clearCache' | 'backup') => {
    const messages: Record<string, string> = {
      clearCache: 'Clear cache? Aplikasi mungkin sedikit lebih lambat untuk beberapa saat.',
      backup: 'Membuat backup SQL database penuh. Proses ini mungkin memerlukan waktu.',
    };

    if (!window.confirm(messages[action])) return;

    setMaintenanceAction(action);
    setMaintenanceLoading(true);
    try {
      if (action === 'clearCache') {
        const response = await adminService.system.clearCache();
        toast.success(response?.message || 'Cache berhasil dibersihkan');
      }

      if (action === 'backup') {
        const response = await adminService.system.backup();
        const backupMeta = response?.data;
        toast.success(
          backupMeta
            ? `${response?.message || 'Backup berhasil dibuat'} ${backupMeta.filename} (${backupMeta.size})`
            : (response?.message || 'Backup berhasil dibuat'),
          { duration: 5000 }
        );
      }

      fetchSystemInfo();
    } catch (e) {
      toast.error('Gagal menjalankan tindakan');
    } finally {
      setMaintenanceLoading(false);
      setMaintenanceAction(null);
    }
  };

  // Handle Clean Reports
  const handleCleanReports = async () => {
    setCleaningData(true);
    try {
      const response = await adminService.system.cleanReports('HAPUS SEMUA DATA');
      toast.success(response?.message || 'Semua data laporan berhasil dihapus');
      setShowCleanDataModal(false);
      setCleanConfirmText('');
      setCleanStep(1);
      fetchSystemInfo();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gagal menghapus data');
    } finally {
      setCleaningData(false);
    }
  };

  const validatePngFile = (file: File, label: string, maxMb = 2) => {
    if (file.type !== 'image/png') {
      toast.error(`${label} harus format PNG transparan`);
      return false;
    }
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Ukuran ${label.toLowerCase()} maksimal ${maxMb}MB`);
      return false;
    }
    return true;
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validatePngFile(file, 'Logo')) { e.target.value = ''; return; }

    setUploadingLogo(true);
    try {
      const res = await adminService.settings.uploadLogo(file);
      const newUrl = res?.data?.logo_url || res?.logo_url;
      if (newUrl) {
        updateSetting('logo_url', newUrl);
        useSystemSettingsStore.getState().updateSettings({ logo_url: newUrl });
      }
      toast.success('Logo berhasil diperbarui');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal mengunggah logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleFaviconChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validatePngFile(file, 'Favicon')) { e.target.value = ''; return; }

    setUploadingFavicon(true);
    try {
      const res = await adminService.settings.uploadFavicon(file);
      const newUrl = res.data?.data?.favicon_url;
      if (newUrl) {
        updateSetting('favicon_url', newUrl);
        useSystemSettingsStore.getState().updateSettings({ favicon_url: newUrl });
      }
      toast.success('Favicon berhasil diperbarui');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal mengunggah favicon');
    } finally {
      setUploadingFavicon(false);
      e.target.value = '';
    }
  };

  const handleLoginBgChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate image
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Background harus format JPG, PNG, atau WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran background maksimal 5MB');
      return;
    }

    setUploadingLoginBg(true);
    try {
      const res = await adminService.settings.uploadLoginBackground(file);
      const newUrl = res.data?.data?.login_background;
      if (newUrl) {
        updateSetting('login_background', newUrl);
        useSystemSettingsStore.getState().updateSettings({ login_background: newUrl });
      }
      toast.success('Background login berhasil diperbarui');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Gagal mengunggah background login');
    } finally {
      setUploadingLoginBg(false);
      e.target.value = '';
    }
  };

  const handleDeleteLoginBg = async () => {
    if (!window.confirm('Hapus background login dan kembali ke default?')) return;

    try {
      await adminService.settings.deleteLoginBackground();
      updateSetting('login_background', '');
      useSystemSettingsStore.getState().updateSettings({ login_background: '' });
      toast.success('Background login telah dihapus');
    } catch (err: any) {
      toast.error('Gagal menghapus background login');
    }
  };

  if (loading) {
    return <Loading variant="page" message="Memuat pengaturan admin..." />;
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Admin', href: '/admin' },
        { label: 'Pengaturan Sistem' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Settings className="w-6 h-6 text-teal-main" />
            Pengaturan Sistem
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Konfigurasi dan preferensi sistem</p>
        </div>
        {activeGroup !== 'maintenance' && (
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Ada perubahan belum disimpan
              </span>
            )}
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              icon={saving ? undefined : <Save className="w-4 h-4" />}
            >
              {saving ? <LoadingLabel label="Menyimpan..." /> : 'Simpan'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: Navigation */}
        <div className="space-y-2">
          {settingGroups.map((group) => {
            const isActive = activeGroup === group.id;
            return (
              <button
                key={group.id}
                onClick={() => setActiveGroup(group.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${isActive
                  ? 'bg-teal-main text-white shadow-lg shadow-teal-main/20'
                  : 'text-neutral-text-secondary hover:bg-neutral-bg hover:text-neutral-text-primary'
                  }`}
              >
                <div className={`p-2 rounded-lg ${isActive ? 'bg-white/20' : 'bg-neutral-bg'
                  }`}>
                  {group.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{group.label}</p>
                  <p className={`text-[10px] truncate ${isActive ? 'text-white/70' : 'text-neutral-text-secondary'
                    }`}>
                    {group.description}
                  </p>
                </div>
                {isActive && <ChevronRight className="w-4 h-4 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Right: Settings */}
        <div className="lg:col-span-3 space-y-6">

          {/* ===== GENERAL ===== */}
          {activeGroup === 'general' && (
            <>
              <Card>
                <SectionHeader
                  icon={<Zap className="w-5 h-5" />}
                  title="Informasi Aplikasi"
                  description="Nama dan deskripsi yang ditampilkan di sistem"
                  color="text-blue-600 bg-blue-100"
                />
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nama Aplikasi"
                      value={settings.app_name}
                      onChange={(e) => updateSetting('app_name', e.target.value)}
                      helpText="Ditampilkan di header & sidebar"
                    />
                    <Input
                      label="Kode Institusi"
                      value={settings.institution_code || ''}
                      onChange={(e) => updateSetting('institution_code', e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                      placeholder="UBP-KRW"
                      helpText="Contoh: UBP-KRW (Dipakai untuk prefix nomor laporan)"
                    />
                  </div>
                  <Input
                    label="Deskripsi"
                    value={settings.app_description}
                    onChange={(e) => updateSetting('app_description', e.target.value)}
                  />
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<Globe className="w-5 h-5" />}
                  title="Informasi Institusi"
                  description="Data universitas untuk dokumen dan laporan"
                  color="text-teal-600 bg-teal-100"
                />
                <div className="space-y-4">
                  <Input label="Nama Institusi" value={settings.institution_name} onChange={(e) => updateSetting('institution_name', e.target.value)} />
                  <Textarea label="Alamat" value={settings.institution_address} onChange={(e) => updateSetting('institution_address', e.target.value)} rows={2} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Telepon" value={settings.institution_phone} onChange={(e) => updateSetting('institution_phone', e.target.value)} />
                    <Input label="Email" type="email" value={settings.institution_email} onChange={(e) => updateSetting('institution_email', e.target.value)} />
                  </div>
                  <Input label="Website" value={settings.institution_website} onChange={(e) => updateSetting('institution_website', e.target.value)} />
                </div>
              </Card>
            </>
          )}

          {/* ===== NOTIFICATION ===== */}
          {activeGroup === 'notification' && (
            <>
              <Card>
                <SectionHeader
                  icon={<Mail className="w-5 h-5" />}
                  title="Konfigurasi Email"
                  description="Pengaturan pengirim email notifikasi"
                  color="text-amber-600 bg-amber-100"
                />
                <div className="space-y-4">
                  <Toggle
                    value={settings.email_enabled}
                    onChange={(v) => updateSetting('email_enabled', v)}
                    label="Aktifkan Notifikasi Email"
                    description="Kirim email untuk setiap perubahan status laporan"
                    icon={<Mail className="w-4 h-4" />}
                  />
                  {settings.email_enabled && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <Input label="Email Pengirim" value={settings.email_from} onChange={(e) => updateSetting('email_from', e.target.value)} />
                        <Input label="Nama Pengirim" value={settings.email_from_name} onChange={(e) => updateSetting('email_from_name', e.target.value)} />
                      </div>
                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={handleTestEmail} disabled={testingEmail}>
                          {testingEmail ? <LoadingLabel label="Mengirim..." /> : 'Tes Kirim Email'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<Bell className="w-5 h-5" />}
                  title="Jenis Notifikasi"
                  description="Pilih event yang akan dikirimkan notifikasi"
                  color="text-amber-600 bg-amber-100"
                />
                <div className="divide-y divide-neutral-border/50">
                  <Toggle value={settings.notif_submission} onChange={(v) => updateSetting('notif_submission', v)} label="Pengajuan Laporan" description="Saat laporan baru diajukan" icon={<FileText className="w-4 h-4" />} />
                  <Toggle value={settings.notif_validation} onChange={(v) => updateSetting('notif_validation', v)} label="Validasi" description="Saat laporan divalidasi atau ditolak" icon={<CheckCircle className="w-4 h-4" />} />
                  <Toggle value={settings.notif_rapim} onChange={(v) => updateSetting('notif_rapim', v)} label="RAPIM" description="Jadwal dan hasil rapat pimpinan" icon={<Clock className="w-4 h-4" />} />
                  <Toggle value={settings.notif_overdue} onChange={(v) => updateSetting('notif_overdue', v)} label="Overdue" description="Saat laporan melewati target" icon={<AlertTriangle className="w-4 h-4" />} />
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<Clock className="w-5 h-5" />}
                  title="Pengingat Otomatis"
                  description="Jadwal pengingat sebelum deadline"
                  color="text-amber-600 bg-amber-100"
                />
                <Input
                  label="Hari Pengingat"
                  value={settings.notif_reminder_days}
                  onChange={(e) => updateSetting('notif_reminder_days', e.target.value)}
                  helpText="Pisahkan dengan koma. Contoh: 7,3,1 = pengingat H-7, H-3, H-1 sebelum deadline"
                  placeholder="7,3,1"
                />
              </Card>
            </>
          )}

          {/* ===== WORKFLOW ===== */}
          {activeGroup === 'workflow' && (
            <>
              <Card>
                <SectionHeader
                  icon={<Clock className="w-5 h-5" />}
                  title="SLA per Tingkat Risiko"
                  description="Batas waktu penyelesaian laporan (dalam hari)"
                  color="text-purple-600 bg-purple-100"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'sla_rendah', label: 'Rendah', color: 'border-l-success', emoji: '🟢' },
                    { key: 'sla_sedang', label: 'Sedang', color: 'border-l-warning', emoji: '🟡' },
                    { key: 'sla_tinggi', label: 'Tinggi', color: 'border-l-orange-400', emoji: '🟠' },
                    { key: 'sla_critical', label: 'Kritis', color: 'border-l-danger', emoji: '🔴' },
                  ].map((sla) => (
                    <div key={sla.key} className={`border-l-4 ${sla.color} pl-3`}>
                      <label className="block text-xs font-semibold text-neutral-text-secondary mb-1.5">
                        {sla.emoji} {sla.label}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          className="w-full px-3 py-2 border border-neutral-border rounded-lg text-sm font-bold text-center focus:ring-2 focus:ring-teal-main/20 focus:border-teal-main transition-colors"
                          value={settings[sla.key]}
                          onChange={(e) => updateSetting(sla.key, parseInt(e.target.value) || 0)}
                        />
                        <span className="text-xs text-neutral-text-secondary shrink-0">hari</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-xs text-blue-700">
                    <Info className="w-3.5 h-3.5 inline mr-1" />
                    SLA harus berurutan dari yang terbesar. Rendah &gt; Sedang &gt; Tinggi &gt; Kritis.
                  </p>
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<Zap className="w-5 h-5" />}
                  title="Eskalasi & Arsip"
                  description="Aturan otomatis untuk eskalasi dan arsip laporan"
                  color="text-purple-600 bg-purple-100"
                />
                <div className="space-y-4">
                  <Toggle value={settings.auto_escalation} onChange={(v) => updateSetting('auto_escalation', v)} label="Eskalasi Otomatis" description="Eskalasi jika tidak ditindaklanjuti dalam waktu tertentu" icon={<AlertTriangle className="w-4 h-4" />} />
                  {settings.auto_escalation && (
                    <div className="pl-11">
                      <Input label="Eskalasi Setelah (hari)" type="number" value={settings.escalation_after_days.toString()} onChange={(e) => updateSetting('escalation_after_days', parseInt(e.target.value))} helpText="Hari tidak ada tindak lanjut sebelum dieskalasi" />
                    </div>
                  )}
                  <div className="space-y-3">
                    <Input
                      label="Auto-arsip Setelah Selesai (hari)"
                      type="number"
                      value={settings.auto_archive_days.toString()}
                      onChange={(e) => updateSetting('auto_archive_days', parseInt(e.target.value))}
                      helpText="Laporan selesai akan otomatis diarsipkan setelah waktu ini"
                    />
                    <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
                      <div className="flex gap-2">
                        <Info className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                        <div className="text-[11px] text-teal-700 leading-relaxed">
                          <p className="font-bold mb-1">Penyimpanan & Pengarsipan Otomatis:</p>
                          <ul className="list-disc pl-4 space-y-1">
                            <li>Laporan berstatus <strong>Selesai</strong> atau <strong>Ditangani Langsung</strong> akan diarsipkan otomatis.</li>
                            <li>Sistem akan mengirimkan notifikasi peringatan ke Admin <strong>7 hari sebelum</strong> pengarsipan dilakukan.</li>
                            <li>Set ke <strong>0</strong> untuk menonaktifkan fitur pengarsipan otomatis.</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<Upload className="w-5 h-5" />}
                  title="Batas Upload File"
                  description="Pembatasan ukuran dan jumlah file lampiran"
                  color="text-purple-600 bg-purple-100"
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input label="Ukuran Maks per File (MB)" type="number" value={settings.max_file_size.toString()} onChange={(e) => updateSetting('max_file_size', parseInt(e.target.value))} />
                  <Input label="Maks File per Laporan" type="number" value={settings.max_files_per_report.toString()} onChange={(e) => updateSetting('max_files_per_report', parseInt(e.target.value))} />
                </div>
              </Card>
            </>
          )}

          {/* ===== SECURITY ===== */}
          {activeGroup === 'security' && (
            <>
              <Card>
                <SectionHeader
                  icon={<Lock className="w-5 h-5" />}
                  title="Kebijakan Password"
                  description="Aturan kekuatan password untuk semua pengguna"
                  color="text-rose-600 bg-rose-100"
                />
                <div className="space-y-4">
                  <div>
                    <Input
                      label="Panjang Minimum"
                      type="number"
                      min={6}
                      max={32}
                      value={settings.password_min_length.toString()}
                      onChange={(e) => updateSetting('password_min_length', Math.min(32, Math.max(6, parseInt(e.target.value) || 6)))}
                      helpText="Minimum 6, maksimum 32 karakter"
                    />
                  </div>

                  <div className="divide-y divide-neutral-border/50">
                    <Toggle
                      value={settings.password_require_uppercase}
                      onChange={(v) => updateSetting('password_require_uppercase', v)}
                      label="Wajib Huruf Besar"
                      description="Password harus mengandung minimal satu huruf kapital (A-Z)"
                      icon={<span className="text-xs font-black">A</span>}
                    />
                    <Toggle
                      value={settings.password_require_number}
                      onChange={(v) => updateSetting('password_require_number', v)}
                      label="Wajib Angka"
                      description="Password harus mengandung minimal satu angka (0-9)"
                      icon={<span className="text-xs font-black">1</span>}
                    />
                    <Toggle
                      value={settings.password_require_special}
                      onChange={(v) => updateSetting('password_require_special', v)}
                      label="Wajib Karakter Spesial"
                      description="Password harus mengandung karakter seperti !@#$%^&*"
                      icon={<span className="text-xs font-black">#</span>}
                    />
                  </div>

                  {/* Preview */}
                  <div className="p-3 bg-neutral-bg rounded-xl border border-neutral-border">
                    <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase mb-2">
                      Preview Kebijakan Aktif
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-white rounded-lg text-xs font-medium border border-neutral-border">
                        Min. {settings.password_min_length} karakter
                      </span>
                      {settings.password_require_uppercase && (
                        <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium border border-teal-200">
                          Huruf Besar ✓
                        </span>
                      )}
                      {settings.password_require_number && (
                        <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium border border-teal-200">
                          Angka ✓
                        </span>
                      )}
                      {settings.password_require_special && (
                        <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium border border-teal-200">
                          Spesial ✓
                        </span>
                      )}
                      {!settings.password_require_uppercase && !settings.password_require_number && !settings.password_require_special && (
                        <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium border border-amber-200">
                          Hanya panjang minimal
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<Eye className="w-5 h-5" />}
                  title="Sesi Login"
                  description="Durasi sesi aktif pengguna"
                  color="text-rose-600 bg-rose-100"
                />
                <Input
                  label="Durasi Logout Otomatis (menit)"
                  type="number"
                  min={5}
                  max={1440}
                  value={settings.session_lifetime.toString()}
                  onChange={(e) => updateSetting('session_lifetime', Math.min(1440, Math.max(5, parseInt(e.target.value) || 120)))}
                  helpText="Pengguna otomatis logout setelah tidak aktif selama durasi ini (5-1440 menit)"
                />
                <div className="mt-3 flex items-center gap-3 text-xs text-neutral-text-secondary">
                  <span className="px-2 py-1 bg-neutral-bg rounded">{settings.session_lifetime} menit</span>
                  <span>=</span>
                  <span className="px-2 py-1 bg-neutral-bg rounded font-medium">
                    {settings.session_lifetime >= 60
                      ? `${Math.floor(settings.session_lifetime / 60)} jam ${settings.session_lifetime % 60 > 0 ? `${settings.session_lifetime % 60} menit` : ''}`
                      : `${settings.session_lifetime} menit`
                    }
                  </span>
                </div>
              </Card>
            </>
          )}

          {/* ===== APPEARANCE ===== */}
          {activeGroup === 'appearance' && (
            <>
              <Card>
                <SectionHeader
                  icon={<Palette className="w-5 h-5" />}
                  title="Logo & Branding"
                  description="Identitas visual sistem"
                  color="text-teal-600 bg-teal-100"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo */}
                  <div className="space-y-3">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                      Logo Utama
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-2xl bg-neutral-bg border-2 border-dashed border-neutral-border flex items-center justify-center overflow-hidden">
                        {settings.logo_url ? (
                          <img src={getAssetUrl(settings.logo_url)} alt="Logo" className="w-full h-full object-contain p-2" />
                        ) : (
                          <span className="text-xs text-neutral-text-secondary">Logo</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          icon={uploadingLogo ? undefined : <Upload className="w-4 h-4" />}
                        >
                          {uploadingLogo ? <LoadingLabel label="Mengunggah..." /> : 'Unggah Logo'}
                        </Button>
                        <p className="text-[10px] text-neutral-text-secondary">PNG transparan, maks 2MB</p>
                      </div>
                    </div>
                    <input ref={logoInputRef} type="file" accept="image/png" className="hidden" onChange={handleLogoChange} />
                  </div>

                  {/* Favicon */}
                  <div className="space-y-3">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                      Favicon
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl bg-neutral-bg border-2 border-dashed border-neutral-border flex items-center justify-center overflow-hidden">
                        {settings.favicon_url ? (
                          <img src={getAssetUrl(settings.favicon_url)} alt="Favicon" className="w-full h-full object-contain p-1" />
                        ) : (
                          <span className="text-[10px] text-neutral-text-secondary">Icon</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => faviconInputRef.current?.click()}
                          disabled={uploadingFavicon}
                          icon={uploadingFavicon ? undefined : <Upload className="w-4 h-4" />}
                        >
                          {uploadingFavicon ? <LoadingLabel label="Mengunggah..." /> : 'Unggah Favicon'}
                        </Button>
                        <p className="text-[10px] text-neutral-text-secondary">PNG transparan, maks 2MB</p>
                      </div>
                    </div>
                    <input ref={faviconInputRef} type="file" accept="image/png" className="hidden" onChange={handleFaviconChange} />
                  </div>

                  {/* Login Background */}
                  <div className="md:col-span-2 space-y-3 border-t border-neutral-border pt-6">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                      Background Halaman Login
                    </label>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-2 relative group aspect-video rounded-2xl bg-neutral-bg border-2 border-dashed border-neutral-border overflow-hidden">
                        {settings.login_background ? (
                          <>
                            <img src={getAssetUrl(settings.login_background)} alt="Login BG" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                              <Button variant="ghost" className="bg-white hover:bg-neutral-bg text-neutral-text-primary" size="sm" onClick={() => loginBgInputRef.current?.click()}>Ganti</Button>
                              <Button variant="danger" size="sm" onClick={handleDeleteLoginBg}>Hapus</Button>
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-neutral-text-secondary">
                            <Palette className="w-8 h-8 mb-2 opacity-20" />
                            <p className="text-xs">Background Default (Gradient Biru)</p>
                          </div>
                        )}
                      </div>
                      <div className="space-y-4">
                        <div className="p-4 bg-teal-50 rounded-xl border border-teal-100">
                          <h4 className="text-xs font-bold text-teal-800 mb-2 flex items-center gap-2">
                            <Info className="w-3.5 h-3.5" /> Tips Background
                          </h4>
                          <ul className="text-[10px] text-teal-700 space-y-1.5 list-disc pl-3">
                            <li>Gunakan gambar landscape (16:9).</li>
                            <li>Resolusi yang disarankan: 1920x1080.</li>
                            <li>Gunakan gambar yang tidak terlalu ramai.</li>
                            <li>Format: JPG, WEBP, atau PNG.</li>
                          </ul>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => loginBgInputRef.current?.click()}
                          disabled={uploadingLoginBg}
                          icon={uploadingLoginBg ? undefined : <Upload className="w-4 h-4" />}
                        >
                          {uploadingLoginBg ? <LoadingLabel label="Mengunggah..." /> : 'Pilih Gambar'}
                        </Button>
                        <input ref={loginBgInputRef} type="file" accept="image/*" className="hidden" onChange={handleLoginBgChange} />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card>
                <SectionHeader
                  icon={<FileText className="w-5 h-5" />}
                  title="Footer"
                  description="Teks yang ditampilkan di bagian bawah sidebar"
                  color="text-teal-600 bg-teal-100"
                />
                <Input
                  label="Teks Footer"
                  value={settings.footer_text}
                  onChange={(e) => updateSetting('footer_text', e.target.value)}
                  placeholder="© 2026 UBP Karawang"
                />
              </Card>
            </>
          )}

          {/* ===== MAINTENANCE ===== */}
          {activeGroup === 'maintenance' && (
            <>
              <Card>
                <SectionHeader
                  icon={<Server className="w-5 h-5" />}
                  title="Informasi Sistem"
                  description="Status dan informasi teknis server"
                  color="text-gray-600 bg-gray-100"
                />
                {systemInfo ? (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                      {[
                        { label: 'Versi Aplikasi', value: systemInfo.app_version, icon: <Zap className="w-4 h-4" />, color: 'bg-blue-50 text-blue-600 border-blue-200' },
                        { label: 'Laravel', value: systemInfo.laravel_version, icon: <Server className="w-4 h-4" />, color: 'bg-red-50 text-red-600 border-red-200' },
                        { label: 'PHP', value: systemInfo.php_version, icon: <Database className="w-4 h-4" />, color: 'bg-purple-50 text-purple-600 border-purple-200' },
                        { label: 'Database', value: systemInfo.db_size, icon: <HardDrive className="w-4 h-4" />, color: 'bg-teal-50 text-teal-600 border-teal-200' },
                        { label: 'Total Users', value: systemInfo.total_users, icon: <Globe className="w-4 h-4" />, color: 'bg-amber-50 text-amber-600 border-amber-200' },
                        { label: 'Total Laporan', value: systemInfo.total_laporan, icon: <FileText className="w-4 h-4" />, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
                        { label: 'Storage', value: systemInfo.storage_used, icon: <HardDrive className="w-4 h-4" />, color: 'bg-orange-50 text-orange-600 border-orange-200' },
                        { label: 'Disk Usage', value: systemInfo.storage_usage_percent != null ? `${systemInfo.storage_usage_percent}%` : '-', icon: <AlertCircle className="w-4 h-4" />, color: 'bg-rose-50 text-rose-600 border-rose-200' },
                        { label: 'Response', value: responseTimeLabel, icon: <Zap className="w-4 h-4" />, color: 'bg-slate-50 text-slate-600 border-slate-200' },
                        { label: 'Uptime', value: uptimeInfo.value, icon: <Clock className="w-4 h-4" />, color: 'bg-green-50 text-green-600 border-green-200' },
                      ].map((item, idx) => (
                        <div key={idx} className={`p-3 rounded-xl border ${item.color}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {item.icon}
                            <span className="text-[10px] font-semibold uppercase">{item.label}</span>
                          </div>
                          <p className="text-sm font-bold">{item.value || '-'}</p>
                        </div>
                      ))}
                    </div>

                    {!uptimeInfo.available && (
                      <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                        <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700">{uptimeInfo.note}</p>
                      </div>
                    )}

                    <div className={`mt-4 rounded-2xl border px-4 py-4 ${healthTone.card}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-xl ${healthTone.pill}`}>
                            {systemHealth.status === 'healthy' ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : systemHealth.status === 'warning' ? (
                              <AlertCircle className="w-5 h-5" />
                            ) : (
                              <AlertTriangle className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className={`text-sm font-bold ${healthTone.text}`}>System Health</p>
                            <p className={`text-xs mt-1 ${healthTone.text}`}>{systemHealth.summary}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${healthTone.pill}`}>
                          {systemHealth.healthyCount} sehat / {systemHealth.issueCount} perhatian
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {systemHealth.checks.map((check) => (
                          <div key={check.key} className="rounded-xl border border-white/70 bg-white/70 px-3 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs font-bold text-neutral-text-primary">{check.label}</p>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${check.status === 'healthy'
                                ? 'bg-green-100 text-green-700'
                                : check.status === 'warning'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                                }`}>
                                {check.status}
                              </span>
                            </div>
                            <p className="text-xs text-neutral-text-secondary mt-1">{check.message}</p>
                            {check.meta && (
                              <p className="text-[11px] text-neutral-text-primary mt-2 font-medium">{check.meta}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                        <p className="text-xs font-semibold uppercase text-blue-700">Backup Engine</p>
                        <p className="text-sm font-bold text-blue-800 mt-1">{systemInfo.backup_method || 'pdo-sql-dump'}</p>
                        <p className="text-xs text-blue-700 mt-2">
                          {systemInfo.backup_note || 'Backup SQL native tanpa ketergantungan exec atau mysqldump.'}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-neutral-border bg-neutral-bg px-4 py-4">
                        <p className="text-xs font-semibold uppercase text-neutral-text-secondary">Backup Terakhir</p>
                        <p className="text-sm font-bold text-neutral-text-primary mt-1">
                          {systemInfo.last_backup?.available
                            ? (systemInfo.last_backup.filename || 'File backup tersedia')
                            : 'Belum ada backup'}
                        </p>
                        <p className="text-xs text-neutral-text-secondary mt-2">
                          {systemInfo.last_backup?.available
                            ? `${systemInfo.last_backup.size || '-'} | ${systemInfo.last_backup.created_at
                              ? new Date(systemInfo.last_backup.created_at).toLocaleString('id-ID')
                              : 'Waktu tidak diketahui'}`
                            : 'Buat backup pertama untuk menyiapkan pemulihan database.'}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <Loading variant="inline" size="sm" message="Memuat informasi sistem..." className="py-4" />
                )}
              </Card>

              <Card>
                <SectionHeader
                  icon={<Wrench className="w-5 h-5" />}
                  title="Maintenance"
                  description="Tindakan pemeliharaan sistem"
                  color="text-gray-600 bg-gray-100"
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    onClick={() => handleMaintenanceAction('clearCache')}
                    disabled={maintenanceLoading}
                    className="p-4 rounded-xl border-2 border-neutral-border hover:border-teal-main/30 hover:bg-teal-50 transition-all text-left group disabled:opacity-50"
                  >
                    <div className="p-2 bg-teal-100 rounded-lg w-fit mb-3 group-hover:bg-teal-200 transition-colors">
                      {maintenanceLoading && maintenanceAction === 'clearCache' ? (
                        <RefreshCw className="w-5 h-5 text-teal-600 animate-spin" />
                      ) : (
                        <RefreshCw className="w-5 h-5 text-teal-600" />
                      )}
                    </div>
                    <p className="text-sm font-bold text-neutral-text-primary">
                      {maintenanceLoading && maintenanceAction === 'clearCache' ? 'Membersihkan Cache...' : 'Clear Cache'}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-1">
                      {maintenanceLoading && maintenanceAction === 'clearCache'
                        ? 'Sistem sedang membersihkan cache konfigurasi.'
                        : 'Hapus cache untuk memperbarui konfigurasi'}
                    </p>
                  </button>

                  <button
                    onClick={() => handleMaintenanceAction('backup')}
                    disabled={maintenanceLoading}
                    className="p-4 rounded-xl border-2 border-neutral-border hover:border-blue-300 hover:bg-blue-50 transition-all text-left group disabled:opacity-50"
                  >
                    <div className="p-2 bg-blue-100 rounded-lg w-fit mb-3 group-hover:bg-blue-200 transition-colors">
                      {maintenanceLoading && maintenanceAction === 'backup' ? (
                        <LogoLoadingMark size="md" />
                      ) : (
                        <HardDrive className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm font-bold text-neutral-text-primary">
                      {maintenanceLoading && maintenanceAction === 'backup' ? 'Membuat Backup...' : 'Backup Database'}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-1">
                      {maintenanceLoading && maintenanceAction === 'backup'
                        ? 'Sistem sedang menyiapkan salinan database.'
                        : 'Buat salinan database untuk keamanan'}
                    </p>
                  </button>

                  {/* Clean Reports */}
                  <button
                    onClick={() => setShowCleanDataModal(true)}
                    disabled={maintenanceLoading}
                    className="p-4 rounded-xl border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-all text-left group disabled:opacity-50"
                  >
                    <div className="p-2 bg-red-100 rounded-lg w-fit mb-3 group-hover:bg-red-200 transition-colors">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-sm font-bold text-red-700">
                      Bersihkan Data Laporan
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-1">
                      Hapus semua laporan, rapat & tindak lanjut. Data master tetap aman.
                    </p>
                  </button>
                </div>

                {/* Warning Box */}
                <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-amber-800 mb-1">Perhatian</h4>
                      <p className="text-xs text-amber-700">
                        Fitur <strong>Bersihkan Data Laporan</strong> akan menghapus semua data transaksi
                        (laporan, validasi, rapat, tindak lanjut) secara permanen. Pastikan sudah melakukan
                        backup terlebih dahulu. Data master seperti user, unit, dan pengaturan sistem tetap aman.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* ===== CLEAN DATA CONFIRMATION MODAL ===== */}
      {showCleanDataModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={!cleaningData ? () => { setShowCleanDataModal(false); setCleanConfirmText(''); setCleanStep(1); } : undefined}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Peringatan: Hapus Data Laporan</h2>
                  <p className="text-red-100 text-sm">Tindakan ini tidak dapat dibatalkan!</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {cleanStep === 1 && (
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <h3 className="font-bold text-red-800 text-sm mb-2 flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />
                      Data yang akan DIHAPUS:
                    </h3>
                    <ul className="text-sm text-red-700 space-y-1 list-disc pl-5">
                      <li>Semua laporan masalah</li>
                      <li>Semua validasi & riwayat validasi</li>
                      <li>Semua rapat pimpinan & agenda</li>
                      <li>Semua tindak lanjut & PPEPP</li>
                      <li>Semua komentar & lampiran</li>
                      <li>Semua notifikasi & log aktivitas</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl">
                    <h3 className="font-bold text-teal-800 text-sm mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Data yang TETAP AMAN:
                    </h3>
                    <ul className="text-sm text-teal-700 space-y-1 list-disc pl-5">
                      <li>Data pengguna (User)</li>
                      <li>Data unit kerja</li>
                      <li>Data role & permission</li>
                      <li>Data periode</li>
                      <li>Pengaturan sistem</li>
                    </ul>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setShowCleanDataModal(false); setCleanConfirmText(''); setCleanStep(1); }}
                    >
                      Batal
                    </Button>
                    <Button
                      variant="danger"
                      className="flex-1"
                      onClick={() => setCleanStep(2)}
                    >
                      Lanjutkan
                    </Button>
                  </div>
                </div>
              )}

              {cleanStep === 2 && (
                <div className="space-y-4">
                  <div className="text-center py-2">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                    <p className="text-neutral-text-primary font-medium">
                      Untuk mengkonfirmasi, ketik teks berikut:
                    </p>
                    <p className="text-red-600 font-mono font-bold text-lg mt-2 select-none">
                      HAPUS SEMUA DATA
                    </p>
                  </div>

                  <Input
                    value={cleanConfirmText}
                    onChange={(e) => setCleanConfirmText(e.target.value.toUpperCase())}
                    placeholder="Ketik konfirmasi di sini..."
                    disabled={cleaningData}
                  />

                  {cleanConfirmText && cleanConfirmText !== 'HAPUS SEMUA DATA' && (
                    <p className="text-xs text-red-600 text-center">
                      Teks tidak sesuai. Ketik persis: HAPUS SEMUA DATA
                    </p>
                  )}

                  {cleanConfirmText === 'HAPUS SEMUA DATA' && (
                    <div className="flex items-center justify-center gap-2 text-green-600 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>Konfirmasi valid</span>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setCleanStep(1)}
                      disabled={cleaningData}
                    >
                      Kembali
                    </Button>
                    <Button
                      variant="danger"
                      className="flex-1"
                      onClick={handleCleanReports}
                      disabled={cleanConfirmText !== 'HAPUS SEMUA DATA' || cleaningData}
                      icon={cleaningData ? undefined : <Trash2 className="w-4 h-4" />}
                    >
                      {cleaningData ? (
                        <LoadingLabel label="Menghapus..." />
                      ) : (
                        'Ya, Hapus Semua'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer warning */}
            <div className="px-6 py-3 bg-amber-50 border-t border-amber-200">
              <p className="text-xs text-amber-700 text-center flex items-center justify-center gap-1">
                <Info className="w-3.5 h-3.5" />
                Pastikan sudah melakukan backup database sebelum menghapus data
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
